import { Icon, FormControl, Checkbox, Tooltip, IconButton } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Flags } from '../../assets';
import {
  EDataTableType,
  ECellType,
  EFeatureStatus,
  ExcludeColumnFilters,
  TableField,
  FeatureTypeFilters,
  ECommonDialogType,
  ERecordLength,
  ERuleSaveStatus,
} from '../../data/Constants';
import { AppDispatch, AppStore } from '../../store/AppStore';
import {
  updateFeatureMarketAssociations,
  updateProductMarketAssociations,
  setFeatureRecordsPerPage,
  resetFeatureMA,
} from '../../store/states/procos';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { openDialog } from '../../store/states/DialogSlice';
import { t } from 'i18next';

import type {
  IMarketAssociation,
  IMarket,
  ICreateCellProps,
  IDialogData,
  IGridColumnContext,
  GridValueGetterParams,
  IMarketColumnContext,
} from '../../../types';

//Dispatches an action to update the market association for the passed country to the passed feature
const updateFeatureMA = (
  event: React.ChangeEvent<HTMLInputElement>,
  code: string,
  marketCode: string,
  featureType: string,
  dispatch: AppDispatch
) => {
  dispatch(
    updateFeatureMarketAssociations({
      id: code,
      market: marketCode,
      featureType: featureType,
      checked: event.target.checked,
    })
  );
};

//Dispatches an action to update the market association for the passed country for Product
const handleQuoteChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  marketCode: string,
  dispatch: AppDispatch
) => {
  dispatch(setFeatureRecordsPerPage(ERecordLength.RL100));
  dispatch(updateProductMarketAssociations({ market: marketCode, checked: event.target.checked }));
};

//To show save status of each feature in table
const showSaveStatusOfFeature = (createCellProps: ICreateCellProps) => {
  const { params } = createCellProps;
  let icon;
  if (params.row?.saveStatus === ERuleSaveStatus.Inprogress) {
    icon = <CheckCircleIcon color="warning" />;
  } else if (params.row?.saveStatus === ERuleSaveStatus.Failed) {
    icon = <CancelIcon color="error" />;
  } else if (params.row?.saveStatus === ERuleSaveStatus.Success) {
    icon = <CheckCircleIcon color="success" />;
  } else if (params.row?.saveStatus === ERuleSaveStatus.Partial) {
    icon = <WarningIcon sx={{ color: 'orange' }} />;
  }

  return <div> {icon || ''} </div>;
};

//Reset Feature Market associations
export const handleResetFeatureMA = (
  id: string | null,
  featureType: string | null,
  dispatch: AppDispatch
) => {
  const data = id
    ? { id: id, type: featureType, message: t('messages.submit.reset') }
    : t('messages.submit.resetAll');

  dispatch(
    openDialog({
      type: ECommonDialogType.Confirm,
      title: t('dialogBox.reset'),
      data,
      handleClickOk: handleConfirmResetFeatureMA,
    })
  );
};

/**
 * This function call action to reset the feature rules
 * @param {object} data
 */
function handleConfirmResetFeatureMA(data: IDialogData | null): void {
  AppStore.dispatch(resetFeatureMA({ id: data?.id, type: data?.type }));
}

// Parses a concatenated "CODE.Description" cell value into separate code and description parts
const getFeatureDetailsFromCell = (cell: string): { code: string; description: string } | null => {
  const [code, ...rest] = cell.split('.');
  const description = rest.join('.').trim();
  if (!code || !description) return null;
  return { code, description };
};

const renderFeatureOrOption = ({ params, col }: ICreateCellProps) => {
  const cell = col.field ? params.row[col.field] : null;
  if (!cell) {
    return null;
  }

  const parsed = getFeatureDetailsFromCell(cell);
  if (!parsed) {
    return null;
  }

  return (
    <>
      <div className="overflow-hidden overflow-ellipsis textWidth50" title={parsed.description}>
        {parsed.description}
      </div>
      <div className="overflow-hidden overflow-ellipsis textWidth50" title={parsed.code}>
        {` (${parsed.code})`}
      </div>
    </>
  );
};

const renderFlag = ({ params }: ICreateCellProps) => (
  <Icon>
    <img src={Flags[params.row.code]} alt="market-flag" />
  </Icon>
);

const renderQuote = ({ params, data, type, isEditMode, procos, dispatch }: ICreateCellProps) => {
  const checked = isMarketChecked(params.row.id, data, type as EDataTableType);
  const viewText = checked ? 'Y' : 'N';
  return !isEditMode || procos.productStatus !== EFeatureStatus.Active ? (
    <span>{viewText}</span>
  ) : (
    <FormControl fullWidth>
      <Checkbox
        className="p-0"
        onChange={(e) => handleQuoteChange(e, params.row['id'], dispatch)}
        checked={checked}
      />
    </FormControl>
  );
};

const renderReset = ({ params, data, isEditMode, procos, dispatch }: ICreateCellProps) => {
  const productMALength = AppStore.getState().procos.product.ma?.updatedMarkets.length ?? 0;
  const allCountriesSelected =
    !data[params?.row['id']] ||
    data[params?.row['id']]?.updatedMarkets.length === 0 ||
    data[params.row.id]?.updatedMarkets.length === productMALength;
  const isDisabled =
    !isEditMode ||
    procos.productStatus !== EFeatureStatus.Active ||
    allCountriesSelected ||
    !params.row.isActive;
  return (
    <Tooltip title={t('tooltip.reset')}>
      <span>
        <IconButton
          className={isDisabled ? 'action-icon-disabled' : 'action-icon'}
          onClick={() => handleResetFeatureMA(params.row['id'], params.row['type'], dispatch)}
          disabled={isDisabled}
        >
          <RestartAltIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const renderStatus = ({ params }: ICreateCellProps) => (
  <div className={params.row.isActive ? 'status-active' : 'status-inactive'}>
    {params.row.isActive ? EFeatureStatus.Active : EFeatureStatus.InActive}
  </div>
);

export const createCell = (props: ICreateCellProps) => {
  const { col, params } = props;

  switch (col.cellType) {
    case ECellType.Feature:
    case ECellType.Option:
      return renderFeatureOrOption(props);

    case ECellType.Flag:
      return renderFlag(props);

    case ECellType.Quote:
      return renderQuote(props);

    case ECellType.Reset:
      return renderReset(props);

    case ECellType.Status:
      return renderStatus(props);

    case ECellType.SaveStatus:
      return showSaveStatusOfFeature(props);

    default:
      return col.field ? params.row[col.field] : null;
  }
};

//Return DataGrid Columns with Table Headers
export const createGridColumns = (context: IGridColumnContext) => {
  const { tableHeaders, data, isEditMode, procos, type, dispatch } = context;
  return tableHeaders.map((header) => {
    const baseColumn = {
      field: header.field,
      headerName:
        header.field === TableField.isActive
          ? t('labels.' + TableField.status)
          : t('labels.' + header.field), //To assign our own custom value in dropdown of filter button in datagrid
      sortable: header.sortable,
      width: header.width,
      headerAlign: header.headerAlign ? header.headerAlign : 'center',
      align: header.align ? header.align : 'left',
      filterable: !ExcludeColumnFilters.includes(header.field ?? ''),
      type: header.field === TableField.type ? 'singleSelect' : null,
      valueOptions: header.field === TableField.type ? FeatureTypeFilters : null,
      renderCell: (params: GridValueGetterParams) =>
        //renderCell is used to render custom component or styling
        createCell({ params, col: header, data, isEditMode, procos, type, dispatch }),
      key: header.field,
    };

    // Conditionally add valueGetter for Status column
    // Values 'Active' or 'Inactive' will be applied for filteration instead of true/false
    if (header.field === TableField.isActive) {
      return {
        ...baseColumn,
        valueGetter: (params: GridRenderCellParams) =>
          params ? EFeatureStatus.Active : EFeatureStatus.InActive,
      };
    }

    // Add valueGetter for Feature/Option columns so filtering uses display format
    if (header.cellType === ECellType.Feature || header.cellType === ECellType.Option) {
      return {
        ...baseColumn,
        valueGetter: (_value: unknown, row: Record<string, string>) => {
          const cell = header.field ? row[header.field] : '';
          if (!cell) return '';
          const parsed = getFeatureDetailsFromCell(cell);
          return parsed ? `${parsed.description}(${parsed.code})` : cell;
        },
      };
    }

    return baseColumn;
  });
};

//Checks if Market is associated for that feature/option/product or not
const isMarketChecked = (
  marketCode: string,
  marketAssociation: IMarketAssociation,
  type: EDataTableType
) => {
  if (
    marketAssociation?.updatedMarkets?.includes(marketCode) ||
    (type === EDataTableType.Features && marketAssociation?.updatedMarkets?.length === 0)
  ) {
    return true;
  }
  return false;
};

//returns the Market columns
export const createMarketColumns = (options: IMarketColumnContext) => {
  const {
    markets,
    marketAssociations: featuresAssociation,
    isEditMode,
    procos,
    dispatch,
  } = options;
  return markets.map((market) => ({
    field: `${TableField.rfq}${market.code}`,
    headerName: t('labels.quoteSymbol'), //To assign our own custom value in dropdown of filter button in datagrid
    width: 65,
    headerAlign: 'center',
    align: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      //renderCell is used to render custom component or styling
      let checked: boolean = false;
      if (params.row.isActive && !featuresAssociation[params.row.id]) {
        checked = true;
      } else if (params.row.isActive) {
        checked = isMarketChecked(
          market.code,
          featuresAssociation[params.row.id],
          EDataTableType.Features
        );
      }
      let checkboxStyle = 'p-1';
      if (!checked && params.row.isActive) {
        checkboxStyle = 'checkbox-highlight p-1';
      }
      const viewText = checked ? 'Y' : 'N';
      return !isEditMode || procos.productStatus !== EFeatureStatus.Active ? (
        <span>{viewText}</span>
      ) : (
        <FormControl>
          <Checkbox
            disabled={!params.row.isActive}
            className={checkboxStyle}
            onChange={(e) =>
              updateFeatureMA(e, params.row.id, market.code, params.row[TableField.type], dispatch)
            }
            checked={checked}
          />
        </FormControl>
      );
    },
  }));
};

//Sets the Grouping Model required for Market Column grouping
export const getGroupingModel = (markets: IMarket[] | null) => {
  return markets?.map((marketCol) => {
    return {
      groupId: marketCol.code,
      renderHeaderGroup: () => {
        return (
          <>
            <Icon>
              <img src={Flags[marketCol.code]} alt="procos-market-flags" />
            </Icon>
            <span style={{ paddingLeft: '3px' }} title={marketCol.name}>
              {marketCol.code}
            </span>
          </>
        );
      },
      children: [{ field: TableField.rfq + marketCol.code }],
    };
  });
};
